import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { Button, Icon, Spinner, Text, useLocalizeMessage } from 'libs.nucleus';
import { SidePanel } from 'libs.react';
import { ChartTypeSection } from './chart_type_section.component';
import { ColorsSection } from './colors_section.component';
import { DataSection } from './data_section/data_section.component';
import { FiltersSection } from './filters_section.component';
import { InfoCardSection } from './info_card_section.component';
import { LabelsSection } from './labels_section.component';
import { OrderSection } from './order_section.component';
import { SortSection } from './sort_section.component';
import { getDefaultPaletteForBuilderChartType, hasValidNumberChartLabels, isBuilderBarChartType, isBuilderLineChartType, } from '../../utils';
export const SettingsPanel = ({ state, onChange, onRun, isRunning, isEditable, chart, onToggle, needsReRun, }) => {
    const translate = useLocalizeMessage();
    const [expandedSections, setExpandedSections] = useState({
        chart: true,
        data: true,
        labels: true,
        colors: true,
        filters: true,
        sort: true,
        order: true,
        infoCard: false,
    });
    const [availableDimensions, setAvailableDimensions] = useState([]);
    const [availableMeasures, setAvailableMeasures] = useState([]);
    const isMissingStudyOrEnv = !state.studyId || !state.testEnvironmentId;
    const isRunDisabled = useMemo(() => {
        const isTable = state.chartType === 'table';
        const isNumberChart = state.chartType === 'number';
        const isBarType = isBuilderBarChartType(state.chartType);
        const isLineType = isBuilderLineChartType(state.chartType);
        const needsSplitBy = state.chartType === 'groupedBar' || state.chartType === 'stackedBar' || state.chartType === 'multiLine';
        if (isTable && (!state.columns || state.columns.length === 0)) {
            return true;
        }
        if (isNumberChart && (!state.numberValues || state.numberValues.length === 0)) {
            return true;
        }
        if (isNumberChart && !hasValidNumberChartLabels(state.numberValues)) {
            return true;
        }
        if ((isBarType || isLineType || state.chartType === 'scatter' || state.chartType === 'pie') &&
            (!state.xAxis || !state.yAxis)) {
            return true;
        }
        if (needsSplitBy && !state.splitBy) {
            return true;
        }
        return false;
    }, [state.chartType, state.columns, state.numberValues, state.xAxis, state.yAxis, state.splitBy]);
    const toggleSection = useCallback((section) => {
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    }, []);
    const handleChartTypeChange = useCallback((type) => {
        const isBarType = isBuilderBarChartType(type);
        const isLineType = isBuilderLineChartType(type);
        const defaultPalette = getDefaultPaletteForBuilderChartType(type);
        const hasShowLabelsOption = type === 'number' || type === 'pie';
        const hasLegendOption = type === 'pie' || type === 'scatter';
        const hideConditionalFormats = type === 'pie';
        // Wipe every data-section field selection on chart-type change. The
        // required roles (dimension vs measure) and the set of relevant slots
        // differ enough between chart types that carrying selections across
        // produces invalid configurations — e.g. a scatter's measure X-axis
        // appearing as a table dimension, or a bar's dimension X-axis being
        // offered to scatter's measure-only X picker.
        onChange({
            ...state,
            chartType: type,
            orientation: isBarType ? state.orientation || 'vertical' : undefined,
            xAxis: undefined,
            yAxis: undefined,
            splitBy: undefined,
            additionalMeasures: type === 'multivariate' ? [] : undefined,
            columns: type === 'table' ? [] : undefined,
            numberValues: type === 'number' ? [] : undefined,
            dimensionOrder: undefined,
            measureOrder: undefined,
            orderRules: undefined,
            defaultSort: undefined,
            showLabels: hasShowLabelsOption ? state.showLabels || false : undefined,
            showLegend: hasLegendOption ? state.showLegend || false : undefined,
            showArea: isLineType ? state.showArea || false : undefined,
            showDots: isLineType ? state.showDots || false : undefined,
            conditionalFormats: hideConditionalFormats ? undefined : state.conditionalFormats,
            palette: defaultPalette || state.palette,
            dotSizeMapping: undefined,
            dotSizeField: undefined,
            dotSizeConstant: undefined,
        });
    }, [state, onChange]);
    const handleOrientationChange = useCallback((orientation) => {
        onChange({ ...state, orientation });
    }, [state, onChange]);
    const handleShowLabelsChange = useCallback((showLabels) => {
        onChange({ ...state, showLabels });
    }, [state, onChange]);
    const handleShowLegendChange = useCallback((showLegend) => {
        onChange({ ...state, showLegend });
    }, [state, onChange]);
    const handleShowAreaChange = useCallback((showArea) => {
        onChange({ ...state, showArea });
    }, [state, onChange]);
    const handleShowDotsChange = useCallback((showDots) => {
        onChange({ ...state, showDots });
    }, [state, onChange]);
    const handleDefaultRowsPerPageChange = useCallback((defaultRowsPerPage) => {
        onChange({ ...state, defaultRowsPerPage });
    }, [state, onChange]);
    const handleRunClick = useCallback(() => {
        if (isMissingStudyOrEnv) {
            setExpandedSections((prev) => ({ ...prev, data: true }));
            const targetId = !state.studyId ? 'chart-study-select' : 'chart-env-select';
            requestAnimationFrame(() => {
                const el = document.getElementById(targetId);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    const focusable = el.querySelector('button, input, [tabindex]');
                    focusable?.focus();
                }
            });
            return;
        }
        onRun();
    }, [isMissingStudyOrEnv, state.studyId, onRun]);
    return (_jsxs(SidePanel, { isOpen: true, side: 'left', widthClassName: 'w-[22rem]', 
        // Sections manage their own horizontal padding, so opt out of the panel's
        // default body padding to keep the existing layout.
        bodyClassName: '', title: translate('Settings panel'), closeWindow: onToggle, footer: _jsx("div", { className: 'h-10', children: _jsx(Button, { label: needsReRun ? `${translate('Run')} *` : translate('Run'), variant: 'primary', onClick: handleRunClick, disabled: isRunDisabled || isRunning, size: 'full', ...(isRunning && { content: _jsx(Spinner, { size: 'sm' }) }) }) }), children: [_jsx(CollapsibleSection, { title: translate('Chart'), isExpanded: expandedSections.chart, onToggle: () => toggleSection('chart'), children: _jsx(ChartTypeSection, { chartType: state.chartType, orientation: state.orientation, showLabels: state.showLabels, showLegend: state.showLegend, showArea: state.showArea, showDots: state.showDots, defaultRowsPerPage: state.defaultRowsPerPage, onChartTypeChange: handleChartTypeChange, onOrientationChange: handleOrientationChange, onShowLabelsChange: handleShowLabelsChange, onShowLegendChange: handleShowLegendChange, onShowAreaChange: handleShowAreaChange, onShowDotsChange: handleShowDotsChange, onDefaultRowsPerPageChange: handleDefaultRowsPerPageChange, isEditable: isEditable }) }), _jsx(CollapsibleSection, { title: translate('Data'), isExpanded: expandedSections.data, onToggle: () => toggleSection('data'), children: _jsx(DataSection, { state: state, onChange: onChange, isEditable: isEditable, 
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    cubeConfigId: chart.cubeConfigId || chart.data?.cubeConfigId, onAvailableDimensionsChange: setAvailableDimensions, onAvailableMeasuresChange: setAvailableMeasures }) }), _jsx(CollapsibleSection, { title: translate('Labels'), isExpanded: expandedSections.labels, onToggle: () => toggleSection('labels'), children: _jsx(LabelsSection, { state: state, onChange: onChange, isEditable: isEditable }) }), _jsx(CollapsibleSection, { title: translate('Colors'), isExpanded: expandedSections.colors, onToggle: () => toggleSection('colors'), children: _jsx(ColorsSection, { state: state, onChange: onChange, isEditable: isEditable, availableDimensions: availableDimensions, availableMeasures: availableMeasures }) }), _jsx(CollapsibleSection, { title: translate('Filters'), isExpanded: expandedSections.filters, onToggle: () => toggleSection('filters'), children: _jsx(FiltersSection, { state: state, onChange: onChange, isEditable: isEditable, availableDimensions: availableDimensions, availableMeasures: availableMeasures }) }), state.chartType === 'table' && (_jsx(CollapsibleSection, { title: translate('Sort'), isExpanded: expandedSections.sort, onToggle: () => toggleSection('sort'), children: _jsx(SortSection, { state: state, onChange: onChange, isEditable: isEditable }) })), state.chartType !== 'number' && state.chartType !== 'pie' && (_jsx(CollapsibleSection, { title: translate('Order'), isExpanded: expandedSections.order, onToggle: () => toggleSection('order'), children: _jsx(OrderSection, { state: state, onChange: onChange, isEditable: isEditable }) })), _jsx(CollapsibleSection, { title: translate('Info card'), isExpanded: expandedSections.infoCard, onToggle: () => toggleSection('infoCard'), children: _jsx(InfoCardSection, { state: state, onChange: onChange, isEditable: isEditable }) })] }));
};
const CollapsibleSection = ({ title, isExpanded, onToggle, children }) => (_jsxs("section", { children: [_jsxs("div", { role: 'button', tabIndex: 0, onClick: onToggle, onKeyDown: (e) => {
                if (e.target === e.currentTarget && (e.code === 'Space' || e.code === 'Enter')) {
                    onToggle();
                }
            }, className: `flex w-full h-11 items-center cursor-pointer select-none px-5 ${isExpanded ? 'bg-neutral-100' : 'bg-white hover:bg-neutral-50'}`, children: [_jsx(Text, { size: 'base', className: 'font-semibold text-neutral-900 flex-1', children: title }), _jsx("div", { className: 'text-neutral-450', children: _jsx(Icon, { name: isExpanded ? 'chevronDown' : 'chevronRight', color: 'current', size: 'sm' }) })] }), isExpanded && _jsx("div", { className: 'px-5 py-4', children: children })] }));
